import useSWR from "swr"

import {
    AppError,
    parseAndThrowAppError,
} from "../../../../errors/errorProcessors"
import { tsIoValidate } from "../../../../utils/general/generalUtils"
import { getClientProtected } from "../../clients/clients"
import {
    GetMyAdminsRes,
    TGetMyAdminsRes,
} from "../../types/organisation/organisationTypes"
import { GET_MY_ADMINS } from "../../urls/private"

interface IUseGetMyAdmins {
    data: string[] | undefined
    isLoading: boolean
    isValidating: boolean
    error: undefined | AppError
    mutate: () => void
}

const mapAdmins = (response: TGetMyAdminsRes): string[] => {
    if (Array.isArray(response)) {
        return response
    }

    if ("org_admins" in response) {
        return response.org_admins
    }

    if ("admins" in response) {
        return response.admins
    }

    if ("orgAdmins" in response) {
        return response.orgAdmins
    }

    return response.organisationAdmins
}

/**
 * Gets the current user's organisation admins.
 * @param enabled - whether the request should be sent
 * @returns {IUseGetMyAdmins} - loading and error states.
 */
const useGetMyAdmins = (enabled: boolean): IUseGetMyAdmins => {
    const { data, error, isLoading, isValidating, mutate } = useSWR(
        enabled ? GET_MY_ADMINS : null,
        async (url: string): Promise<string[]> => {
            try {
                const res = await getClientProtected(url)
                const validatedRes = tsIoValidate<TGetMyAdminsRes>(
                    GetMyAdminsRes,
                    res,
                    "Invalid organisation admins received from server"
                )
                return mapAdmins(validatedRes)
            } catch (err) {
                throw parseAndThrowAppError(err)
            }
        }
    )

    return {
        data,
        isLoading,
        isValidating,
        error,
        mutate,
    }
}

export default useGetMyAdmins
